/**
 * Категории единиц измерения — русские подписи и порядок групп (AK-704 п.3).
 *
 * UAT founder 20.08: «в селекторе категорий нет „Количество“ (шт, тыс. шт,
 * млн шт, п.е.) и нет „Массы“ (г, кг, т)». Разбор показал ДВЕ параллельные
 * таблицы единиц:
 *
 *  • `reference.measurement_units` — КАНОН: на него стоят все ссылки
 *    (`budget_catalog_items.unit_id` / `price_unit_id`, `company_tmc_items`,
 *    `tech_card_materials.rate_unit_id`). Категории: mass, volume, area,
 *    length, time, count, packaging, rate, concentration, microbio. Здесь
 *    есть и г/кг/ц/т, и шт/тыс. шт/млн шт, и «п.е.»;
 *  • `reference.units` — реестр единиц ТЕХ.НОРМАТИВОВ (л/га, кг/га, км/ч, °C)
 *    с собственной таксономией из восьми категорий (длина, скорость,
 *    объём/площадь, масса/площадь, плотность, температура, доля, время). Ни
 *    «кг», ни «шт», ни «п.е.» в ней нет и по смыслу быть не может.
 *
 * Восемь категорий, которые видел founder, — таксономия ВТОРОЙ таблицы
 * (экран платформенного админа «Единицы измерения»). Дописать в неё «Массу» и
 * «Количество» значило бы завести третью версию правды и дать заводить кг/шт
 * дублями к канону. Поэтому здесь — подписи КАНОНА, и группируем ими тот
 * список, из которого единица реально выбирается в бюджетном справочнике.
 * Разведение двух реестров — отдельное решение уровня ADR (владелец
 * `reference.*` — domain-reference), см. описание PR.
 *
 * Порядок групп — не алфавитный, а рабочий: чем чаще экономист выбирает
 * единицу этой природы в бюджете, тем выше. Алфавит остаётся ВНУТРИ группы.
 */

/** Русские подписи категорий канона. Ключ — `measurement_units.category`. */
export const UNIT_CATEGORY_LABELS: Record<string, string> = {
  mass: 'Масса',
  count: 'Количество',
  packaging: 'Фасовка',
  volume: 'Объём',
  area: 'Площадь',
  length: 'Длина',
  time: 'Время',
  rate: 'Норма расхода',
  concentration: 'Концентрация',
  microbio: 'Микробиология',
};

/** Рабочий порядок групп в выпадашке. */
export const UNIT_CATEGORY_ORDER: readonly string[] = [
  'mass',
  'count',
  'packaging',
  'volume',
  'area',
  'length',
  'time',
  'rate',
  'concentration',
  'microbio',
];

/**
 * Подпись группы. Незнакомая категория (её мог завести platform-admin) не
 * прячется и не подписывается кодом из модели — «Прочее» честнее, чем
 * «microbio_v2» в интерфейсе агронома, и честнее, чем пропавшая единица.
 */
export function unitCategoryLabel(category: string | null | undefined): string {
  if (!category) return 'Прочее';
  return UNIT_CATEGORY_LABELS[category] ?? 'Прочее';
}

/**
 * Разложить УЖЕ отранжированный список по группам, сохранив порядок внутри.
 *
 * Принимает результат ранжирования, а не сырой список: поиск обязан остаться
 * главным (founder ищет «п.е.» и должен её найти), а группы — только способ
 * разложить то, что нашлось. Пустые группы не возвращаются.
 */
export function groupUnitsByCategory<T extends { category?: string | null }>(
  units: readonly T[],
): { key: string; label: string; units: T[] }[] {
  const buckets = new Map<string, T[]>();
  for (const unit of units) {
    const key = unit.category && UNIT_CATEGORY_LABELS[unit.category] ? unit.category : '__other__';
    const bucket = buckets.get(key);
    if (bucket) bucket.push(unit);
    else buckets.set(key, [unit]);
  }
  const rank = (key: string) => {
    const index = UNIT_CATEGORY_ORDER.indexOf(key);
    // «Прочее» всегда в конце: это не категория, а место для неожиданного.
    return index === -1 ? UNIT_CATEGORY_ORDER.length : index;
  };
  return [...buckets.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([key, list]) => ({
      key,
      label: key === '__other__' ? 'Прочее' : unitCategoryLabel(key),
      units: list,
    }));
}
